import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { AlarmsByType, DashboardSummary, DashboardStation } from '@/types/station'
import { getSummary, getStations, getStationStatusSummary, type StationStatusSummary } from '@/api/dashboard'
import { getProtectorAlarmLog } from '@/api/alarms'
import { t } from '@/i18n'

export const useDashboardStore = defineStore('dashboard', () => {
  // State
  const summary = ref<DashboardSummary | null>(null)
  const stations = ref<DashboardStation[]>([])
  const loading = ref(false)
  const refreshing = ref(false)  // สำหรับ polling refresh (ไม่ใช่ initial load)
  const error = ref<string | null>(null)
  const lastUpdated = ref<Date | null>(null)

  // Pagination state (client-side, 12 items per page)
  const page = ref(1)
  const perPage = ref(12)

  // สรุปสถานะจาก tb_station_state (ground truth) — count total/alert/warning/
  // normal + donut ราย alarm type มาจาก /station-status-summary/
  const stationStatus = ref<StationStatusSummary | null>(null)
  const alarmsByType = ref<AlarmsByType>({ gfr: 0, ovpr: 0, uvpr: 0 })
  const totalStationsWithAlarm = ref(0)

  // Getters
  const activeStations = computed(() =>
    stations.value.filter((s) => s.status === 'active')
  )

  const offlineStations = computed(() =>
    stations.value.filter((s) => s.status === 'offline')
  )

  // "ออนไลน์" = ยังส่งข้อมูลเข้ามาอยู่ ไม่เกี่ยวกับว่ามี alert/warning หรือไม่
  // สถานีที่แจ้งเตือนอยู่ก็ยังออนไลน์ จึงต้องนับรวมด้วย (ใช้เป็น fallback ตอน
  // stationStatus ยังโหลดไม่เสร็จเท่านั้น ตัวจริงมาจาก backend)
  const onlineStations = computed(() =>
    stations.value.filter((s) => s.status !== 'offline')
  )

  const warningStations = computed(() =>
    stations.value.filter((s) => s.status === 'warning')
  )

  // เช่น "จากทั้งหมด 21 สถานี ปกติกี่สถานี ALERT กี่สถานี" — นับจาก
  // station.status ที่รวม severity (tb_station_state) เข้ากับ freshness แล้ว
  const alertStations = computed(() =>
    stations.value.filter((s) => s.status === 'alert')
  )

  // Pagination computed
  const total = computed(() => stations.value.length)
  const totalPages = computed(() => Math.ceil(total.value / perPage.value))
  const paginatedStations = computed(() => {
    const start = (page.value - 1) * perPage.value
    const end = start + perPage.value
    return stations.value.slice(start, end)
  })

  // count (total/warning/alarm/normal) ยึดจาก tb_station_state ผ่าน stationStatus
  // — power/energy ยังรวมจาก station list; fallback เป็น count จาก list ถ้ายัง
  // โหลด stationStatus ไม่เสร็จ
  const computedSummary = computed<DashboardSummary>(() => {
    const totalPowerKw = stations.value.reduce((sum, s) => sum + s.power_kw, 0)
    const totalEnergyKwh = stations.value.reduce((sum, s) => sum + s.energy_kwh, 0)
    const st = stationStatus.value
    const totalStations = st ? st.total_stations : stations.value.length
    const warningCount = st ? st.stations_warning : warningStations.value.length
    const alarmCount = st ? st.stations_alert : alertStations.value.length

    // Active Stations = สถานีที่ "ออนไลน์" (ยังส่งข้อมูลเข้ามา) เท่านั้น
    // ไม่หัก alert/warning ออก เพราะสถานีที่แจ้งเตือนอยู่ก็ยังออนไลน์
    // ตัวเดิมใช้ stations_normal ซึ่งหักทั้ง alert/warning/offline ทำให้เลข
    // ตกลงทุกครั้งที่มี alarm ทั้งที่การเชื่อมต่อยังปกติดี
    const onlineCount = st ? st.stations_online : onlineStations.value.length
    const healthPercent = totalStations > 0 ? (onlineCount / totalStations) * 100 : 100

    return {
      total_power_kw: totalPowerKw,
      total_energy_kwh: totalEnergyKwh,
      active_stations: onlineCount,
      warning_stations: warningCount,
      total_stations: totalStations,
      total_alarms: alarmCount,
      alarms_by_type: alarmsByType.value,
      health_percent: healthPercent,
    }
  })

  // Actions
  async function fetchSummary() {
    try {
      summary.value = await getSummary()
    } catch (e) {
      error.value = t('Failed to fetch summary')
      throw e
    }
  }

  async function fetchStations() {
    loading.value = true
    error.value = null

    try {
      stations.value = await getStations()
      lastUpdated.value = new Date()
    } catch (e) {
      error.value = t('Failed to fetch stations')
      throw e
    } finally {
      loading.value = false
    }
  }

  async function fetchStationStatus() {
    try {
      const result = await getStationStatusSummary()
      stationStatus.value = result
      totalStationsWithAlarm.value = result.stations_alert
      // by_type ไม่เอาจาก station_state (เก็บแค่ alarm type "ล่าสุด" ต่อสถานี
      // ไม่ครบทุกตัวที่ active) — ไปนับจาก active alarm log แทน (fetchAlarmsByType)
      // ให้ตรงกับหน้า Alarm เป๊ะ
    } catch (e) {
      error.value = t('Failed to fetch station status')
      throw e
    }
  }

  // donut GFR/OVPR/UVPR = นับ "รายการ" alarm ที่ active จาก log จริง (แหล่ง
  // เดียวกับหน้า Alarm) ไม่ใช่ station_state ที่เก็บแค่ type ล่าสุดต่อสถานี
  async function fetchAlarmsByType() {
    try {
      // per_page: 1 เพราะใช้แค่ summary — backend นับให้จากทั้งตารางอยู่แล้ว
      // ไม่ต้องดึงแถวมานับเองซึ่งจะพลาด alarm ที่หลุดออกนอกหน้าแรก
      const { summary } = await getProtectorAlarmLog({ active_only: true, per_page: 1 })
      alarmsByType.value = { ...summary.by_type }
    } catch {
      // ไม่ให้ล้ม refresh ทั้งก้อนเพราะ donut
    }
  }

  async function refresh() {
    // count/สถานะ มาจาก tb_station_state (fetchStationStatus) — station list
    // (fetchStations) เอาไว้โชว์รายละเอียด/power/energy ต่อสถานี — donut by-type
    // จาก active alarm log (fetchAlarmsByType)
    refreshing.value = true
    try {
      await Promise.all([fetchStations(), fetchStationStatus(), fetchAlarmsByType()])
    } finally {
      refreshing.value = false
    }
  }

  function setPage(newPage: number) {
    if (newPage >= 1 && newPage <= totalPages.value) {
      page.value = newPage
    }
  }

  return {
    // State
    summary,
    stations,
    stationStatus,
    loading,
    refreshing,
    error,
    lastUpdated,
    // Pagination
    page,
    perPage,
    total,
    totalPages,
    paginatedStations,
    // Getters
    activeStations,
    offlineStations,
    onlineStations,
    warningStations,
    alertStations,
    computedSummary,
    // Actions
    fetchSummary,
    fetchStations,
    fetchStationStatus,
    fetchAlarmsByType,
    refresh,
    setPage,
  }
})
